import { TransactionDetailsDrawer } from '@/features/InvoiceReport/Edit/components/UtilizePastDetails/TransactionDetailsDrawer';
import { InvoiceTransaction } from '@/features/InvoiceReport/Edit/type';
import { useTranslation } from '@/i18n';
import { ButtonV2, Tooltip } from '@moneyforward/ap-frontend-components';
import { ApPayeeDetail as ApPayeeModel } from 'ap-openapi';
import { FC, memo, Suspense, useCallback } from 'react';
import { PROGRESS_STEP } from './constants';
import {
  DataProvider,
  useDataContext,
} from './context/UtilizePastDetailsDataContext';

import { PaymentRequestsDialog } from './Dialog';
import { useUtilizePastDetails } from './hooks/useUtilizePastDetails';

type Props = {
  selectedPayee?: ApPayeeModel;
  onImport: (items: InvoiceTransaction[]) => void;
  showColumnDetails?: boolean;
  canDisplayDrExcise?: boolean;
};

export const InnerUtilizePastDetails: FC<Props> = memo(
  ({ selectedPayee, onImport, showColumnDetails, canDisplayDrExcise }) => {
    const { t } = useTranslation();
    const { tooltipTitle, hasSelectedPayee } =
      useUtilizePastDetails(selectedPayee);
    const { onSetCurrentStep, onSetSelectedFile } = useDataContext();

    const handleClick = useCallback(() => {
      onSetSelectedFile(null);
      onSetCurrentStep(PROGRESS_STEP.ON_OPEN_DIALOG);
    }, [onSetCurrentStep, onSetSelectedFile]);

    return (
      <>
        <div>
          <Tooltip title={tooltipTitle} placement='top'>
            <ButtonV2
              isSecondary
              onClick={handleClick}
              isTertiary
              isDisabled={!hasSelectedPayee}
            >
              {t('payment_past_details_utilize_past_details')}
            </ButtonV2>
          </Tooltip>
        </div>
        <PaymentRequestsDialog selectedPayee={selectedPayee} />
        <TransactionDetailsDrawer
          selectedPayee={selectedPayee}
          onImport={onImport}
          showColumnDetails={showColumnDetails}
          canDisplayDrExcise={canDisplayDrExcise}
        />
      </>
    );
  }
);
InnerUtilizePastDetails.displayName = 'InnerUtilizePastDetails';

export const UtilizePastDetails: FC<Props> = memo(
  ({ selectedPayee, onImport, showColumnDetails, canDisplayDrExcise }) => {
    return (
      <Suspense fallback={<ButtonV2 isLoading />}>
        <DataProvider>
          <InnerUtilizePastDetails
            selectedPayee={selectedPayee}
            onImport={onImport}
            showColumnDetails={showColumnDetails}
            canDisplayDrExcise={canDisplayDrExcise}
          />
        </DataProvider>
      </Suspense>
    );
  }
);
UtilizePastDetails.displayName = 'UtilizePastDetails';
